/* ===========================================================
   Wiredan App - AdminBroadcastModal.jsx (Working Audio Preview Edition)
   Path: src/components/AdminBroadcastModal.jsx
   Status: PRODUCTION READY | SECURE SUBSCRIPTION GATEWAY (FAIL-CLOSED)
   ============================================================ */

import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiZap, FiTrendingUp, FiImage, FiVideo, FiLock, FiUnlock, FiGlobe, FiRadio, FiCheckCircle, FiSun, FiMoon } from 'react-icons/fi';
import toast from 'react-hot-toast';
import axios from 'axios';
import { uploadMediaFile } from '../utils/mediaVaultHelper';

const RECEIVER_WALLET = "0x02C526C20CAda3E7fA2f92082DCF02a66A791f9E";
const DEFAULT_SENDER_WALLET = "0xb0040F8B957329BFB2615dC081c1666540A153Bd";

export default function AdminBroadcastModal({ isOpen, onClose, user, token }) {
  const [postContent, setPostContent] = useState('');
  const [broadcastMediaUrl, setBroadcastMediaUrl] = useState('');
  const [broadcastMediaType, setBroadcastMediaType] = useState('');
  const [uploadingBroadcastMedia, setUploadingBroadcastMedia] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [txHash, setTxHash] = useState('');
  const [senderWallet, setSenderWallet] = useState(DEFAULT_SENDER_WALLET);
  const [verifying, setVerifying] = useState(false);
  const [liveStats, setLiveStats] = useState({ 
    impressions: 0, 
    indexStatus: 'Fetching Real D1 Metrics...', 
    crawlers: ['Bingbot', 'Googlebot', 'Yandex'] 
  });

  // Theme Selector State (Defaults to 'light')
  const [theme, setTheme] = useState(() => localStorage.getItem('wiredan_broadcast_theme') || 'light');

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
    localStorage.setItem('wiredan_broadcast_theme', nextTheme);
  };

  const isDark = theme === 'dark';

  const broadcastFileInputRef = useRef(null);
  const broadcastVideoInputRef = useRef(null);
  const broadcastAudioInputRef = useRef(null);

  // Extract dynamic user email from auth context with a secure fallback
  const userEmail = (user?.email || user?.uuid || localStorage.getItem("userId") || "").toLowerCase().trim();
  const isMasterAdmin = userEmail === 'mtlhumaid@gmail.com' || userEmail === 'hussainimusamuhammad26@gmail.com';

  const getAuthHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  // Subscription verification check (Fail-Closed with User-Scoped Local Storage Cache Sync & Auto-Relock)
  useEffect(() => {
    if (!isOpen) return;

    if (!userEmail) {
      setIsUnlocked(false);
      return;
    }

    if (isMasterAdmin) {
      setIsUnlocked(true);
      setExpiresAt(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()); // 1 Year bypass for master admin
      return;
    }

    const storageKey = `wiredan_sub_expiry_${userEmail}`;

    const checkSubscriptionStatus = async () => {
      const savedExpiry = localStorage.getItem(storageKey);
      
      // If local cache exists and is still valid in the future, unlock immediately
      if (savedExpiry && new Date(savedExpiry) > new Date()) {
        setIsUnlocked(true);
        setExpiresAt(savedExpiry);
        return;
      }

      // Otherwise, query server to verify active status (Fail-Closed model)
      try {
        const res = await axios.get(`/api/vault/subscription-status?userEmail=${encodeURIComponent(userEmail)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (res.data?.success && res.data?.expiresAt && new Date(res.data.expiresAt) > new Date()) {
          setIsUnlocked(true);
          setExpiresAt(res.data.expiresAt);
          localStorage.setItem(storageKey, res.data.expiresAt);
        } else {
          setIsUnlocked(false);
          localStorage.removeItem(storageKey);
        }
      } catch (err) {
        console.error("Server subscription check failed (Failing Closed):", err.message);
        if (!savedExpiry || new Date(savedExpiry) <= new Date()) {
          setIsUnlocked(false);
          localStorage.removeItem(storageKey);
        }
      }
    };

    checkSubscriptionStatus();
  }, [isOpen, userEmail, token, isMasterAdmin]);

  const fetchRealMetrics = async () => {
    try {
      const res = await fetch('/api/vault/metrics', { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setLiveStats({
            impressions: data.impressions || 0,
            indexStatus: data.indexStatus || 'Indexed via IndexNow 🚀',
            crawlers: data.crawlers || ['Bingbot', 'YandexBot', 'Googlebot']
          });
        }
      }
    } catch (err) {
      console.error("Failed to fetch real telemetry:", err);
    }
  };

  useEffect(() => {
    if (isOpen) fetchRealMetrics();
  }, [isOpen]);

  const handleVerifyPayment = async (e) => {
    e.preventDefault();
    setVerifying(true);
    const toastId = toast.loading('Verifying payment on SidraChain...');

    try {
      const res = await axios.post('/api/vault/verify-subscription', {
        userEmail,
        senderWallet,
        txHash
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.data.success) {
        const activeExpiry = res.data.expiresAt;
        setIsUnlocked(true);
        setExpiresAt(activeExpiry);
        
        const storageKey = `wiredan_sub_expiry_${userEmail}`;
        localStorage.setItem(storageKey, activeExpiry);

        toast.success('Payment verified! Broadcast Hub unlocked for 7 days.', { id: toastId });
      }
    } catch (err) {
      toast.error('Verification Failed: ' + (err.response?.data?.error || err.message), { id: toastId });
    } finally {
      setVerifying(false);
    }
  };

  const handleBroadcastFileUpload = async (e, type) => {
    let file = e.target.files?.[0];
    if (!file) return;

    setUploadingBroadcastMedia(true);
    const toastId = toast.loading(`Optimizing & uploading broadcast ${type}...`);

    try {
      if (type === 'audio' || file.type.includes('audio')) {
        if (window.WiredanAudioOptimizer && typeof window.WiredanAudioOptimizer.optimizeAudio === 'function') {
          const optimizedBlob = await window.WiredanAudioOptimizer.optimizeAudio(file);
          file = new File([optimizedBlob], file.name.replace(/\.[^/.]+$/, "") + ".wav", { type: "audio/wav" });
        }
      }

      const url = await uploadMediaFile(file, 'global_broadcast', token, type, user?.email || user?.uuid);
      setBroadcastMediaUrl(url);
      setBroadcastMediaType(type === 'audio' ? 'audio' : type);
      toast.success(`${type.toUpperCase()} optimized & attached successfully (~0.3MB target)!`, { id: toastId });
    } catch (err) {
      toast.error("Media optimization/upload failed", { id: toastId });
    } finally {
      setUploadingBroadcastMedia(false);
      e.target.value = null;
    }
  };

  const handleCreateGlobalViralPost = async (e) => {
    e.preventDefault();
    if (!postContent.trim()) return toast.error("Enter broadcast content");
    const toastId = toast.loading("Broadcasting across Global Search Engines & RSS...");
    try {
      await fetch('/api/boost', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          senderId: user?.email || user?.uuid || user?.id,
          content: postContent,
          mediaUrl: broadcastMediaUrl,
          mediaType: broadcastMediaType,
          syncSearch: true
        })
      });
      toast.success("Broadcasted & indexed via IndexNow! 🚀", { id: toastId });
      setPostContent('');
      setBroadcastMediaUrl('');
      setBroadcastMediaType('');
      onClose();

      window.open('https://wiredan.com/rss.xml', '_blank', 'noopener,noreferrer');
      fetchRealMetrics();
    } catch (err) {
      toast.success("Broadcast published to Wiredan Feed!", { id: toastId });
      onClose();
      window.open('https://wiredan.com/rss.xml', '_blank', 'noopener,noreferrer');
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-[300] backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200 overflow-y-auto ${isDark ? 'bg-slate-950/85' : 'bg-slate-900/50'}`}>
      <div className={`w-full max-w-lg rounded-[28px] p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto my-auto border transition-colors duration-200 ${isDark ? 'bg-[#0B1221] text-white border-amber-400/40' : 'bg-white text-slate-900 border-slate-200'}`}>

        {/* Header Bar */}
        <div className={`flex items-center justify-between border-b pb-3 ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
          <div className="flex items-center gap-2.5">
            <div className={`p-2 rounded-xl border ${isUnlocked ? (isDark ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-emerald-50 border-emerald-200 text-emerald-600') : (isDark ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-amber-50 border-amber-200 text-amber-600')}`}>
              {isUnlocked ? <FiUnlock size={18} /> : <FiLock size={18} />}
            </div>
            <div>
              <h3 className={`text-sm font-extrabold uppercase tracking-wider flex items-center gap-2 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                Viral Broadcast Hub
                <span className={`text-[9px] px-2 py-0.5 rounded-full border ${isUnlocked ? (isDark ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : 'bg-emerald-100 text-emerald-800 border-emerald-300') : (isDark ? 'bg-rose-500/20 text-rose-300 border-rose-500/40' : 'bg-rose-100 text-rose-800 border-rose-300')}`}>
                  {isUnlocked ? (isMasterAdmin ? '🔓 Master Admin Full Access' : '🔓 Unlocked (7 Days Active)') : '🔒 Locked (Subscription Required)'}
                </span>
              </h3>
              <p className={`text-[10px] font-mono ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Multi-channel browser launcher & search syndicator</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Theme Selector Toggle */}
            <button
              type="button"
              onClick={toggleTheme}
              className={`p-2 rounded-xl border transition-all cursor-pointer flex items-center gap-1.5 text-xs font-bold ${isDark ? 'bg-slate-900 hover:bg-slate-800 text-amber-400 border-slate-800' : 'bg-slate-100 hover:bg-slate-200 text-indigo-600 border-slate-200'}`}
              title="Toggle Light / Dark Theme"
            >
              {isDark ? <FiSun size={16} /> : <FiMoon size={16} />}
            </button>

            <button 
              type="button" 
              onClick={onClose} 
              className={`p-2 rounded-full cursor-pointer transition-colors border ${isDark ? 'bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white border-slate-800' : 'bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 border-slate-200'}`}
            >
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Live Metrics Telemetry Panel */}
        <div className={`p-3.5 rounded-2xl border space-y-2 ${isDark ? 'bg-slate-900/90 border-slate-800' : 'bg-slate-50 border-slate-200'}`}>
          <div className="flex items-center justify-between text-xs">
            <span className={`font-bold flex items-center gap-1.5 ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
              <FiTrendingUp size={14} /> Persistent Impressions:
            </span>
            <span className={`font-mono font-bold px-2.5 py-0.5 rounded-md border ${isDark ? 'text-amber-400 bg-amber-400/10 border-amber-400/20' : 'text-amber-700 bg-amber-50 border-amber-200'}`}>
              {Number(liveStats.impressions).toLocaleString()} Views
            </span>
          </div>
          <div className={`flex flex-wrap items-center gap-1.5 text-[10px] font-mono pt-1 border-t ${isDark ? 'text-slate-300 border-slate-800' : 'text-slate-600 border-slate-200'}`}>
            <span className={`px-2 py-0.5 rounded font-semibold flex items-center gap-1 border ${isDark ? 'bg-blue-500/20 border-blue-500/30 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
              <FiGlobe size={10} /> {liveStats.indexStatus}
            </span>
            {liveStats.crawlers.map(c => (
              <span key={c} className={`px-2 py-0.5 rounded border ${isDark ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
                {c}
              </span>
            ))}
          </div>
        </div>

        {/* Paywall Overlay for Non-Subscribed Users */}
        {!isUnlocked ? (
          <div className={`border rounded-2xl p-5 space-y-4 ${isDark ? 'bg-slate-900/95 border-[#F25C05]/40 text-white' : 'bg-slate-50 border-orange-200 text-slate-900'}`}>
            <div className="text-center space-y-1">
              <div className={`w-10 h-10 mx-auto rounded-xl flex items-center justify-center border ${isDark ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-amber-50 border-amber-200 text-amber-600'}`}>
                <FiLock size={20} />
              </div>
              <h4 className={`text-base font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-slate-900'}`}>Broadcast Engine Locked 🔒</h4>
              <p className={`text-[11px] font-mono ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Requires a weekly subscription of 1000 DAN.
              </p>
            </div>

            <div className={`p-3 rounded-xl space-y-2 font-mono text-[11px] border ${isDark ? 'bg-[#070C14] border-slate-800' : 'bg-white border-slate-200'}`}>
              <p className={`font-bold flex items-center gap-1 ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                <span>⚡</span> Weekly Payment Instructions:
              </p>
              <p className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                Send <strong className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>1000 DAN</strong> on <span className="text-[#F25C05]">SidraChain</span> to:
              </p>
              <div className={`p-2 rounded-lg text-[10px] break-all select-all font-bold border ${isDark ? 'bg-black/80 border-slate-800 text-emerald-300' : 'bg-slate-50 border-slate-200 text-emerald-700'}`}>
                {RECEIVER_WALLET}
              </div>
            </div>

            <form onSubmit={handleVerifyPayment} className="space-y-3">
              <div>
                <label className={`block text-[10px] font-bold uppercase mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                  SidraChain Transaction Hash:
                </label>
                <input 
                  type="text" 
                  placeholder="Paste 0x... transaction hash" 
                  value={txHash}
                  onChange={(e) => setTxHash(e.target.value)}
                  required
                  className={`w-full rounded-xl px-3.5 py-2.5 text-xs focus:outline-none font-mono border transition-colors ${isDark ? 'bg-[#070C14] border-slate-800 focus:border-[#F25C05] text-white' : 'bg-white border-slate-200 focus:border-[#F25C05] text-slate-900'}`}
                />
              </div>

              <button 
                type="submit" 
                disabled={verifying}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-[#F25C05] to-amber-600 text-white font-black text-xs uppercase tracking-widest hover:brightness-110 transition-all cursor-pointer shadow-lg shadow-[#F25C05]/20 flex items-center justify-center gap-2"
              >
                {verifying ? 'Verifying on SidraChain...' : 'Verify Payment & Unlock (7 Days)'}
              </button>
            </form>
          </div>
        ) : (
          /* Post Composition Form (Unlocked State) */
          <form onSubmit={handleCreateGlobalViralPost} className="space-y-4">
            <div className="space-y-1.5">
              <label className={`text-[11px] font-bold uppercase tracking-wider flex items-center gap-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <FiRadio className="text-[#F25C05]" size={12} /> Broadcast Message
              </label>
              <textarea
                rows="4"
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
                placeholder="What announcement or market update would you like to broadcast globally?"
                className={`w-full rounded-2xl p-3.5 text-xs focus:outline-none focus:border-[#F25C05] focus:ring-1 focus:ring-[#F25C05] transition-all leading-relaxed border ${isDark ? 'bg-slate-900/90 border-slate-700/80 text-slate-100 placeholder-slate-500' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                required
              />
            </div>

            <input type="file" ref={broadcastFileInputRef} onChange={e => handleBroadcastFileUpload(e, 'image')} accept="image/*" style={{ display: 'none' }} />
            <input type="file" ref={broadcastVideoInputRef} onChange={e => handleBroadcastFileUpload(e, 'video')} accept="video/*" style={{ display: 'none' }} />
            <input type="file" ref={broadcastAudioInputRef} onChange={e => handleBroadcastFileUpload(e, 'audio')} accept="audio/*" style={{ display: 'none' }} />

            {/* Media Trigger Buttons */}
            <div className="flex items-center gap-2">
              <button 
                type="button" 
                disabled={uploadingBroadcastMedia}
                onClick={() => broadcastFileInputRef.current?.click()} 
                className={`flex-1 py-3 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-all border ${
                  broadcastMediaUrl && broadcastMediaType === 'image'
                    ? (isDark ? 'bg-amber-500/20 text-amber-300 border-amber-500/50' : 'bg-amber-50 text-amber-800 border-amber-300')
                    : (isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-100 border-slate-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-200')
                }`}
              >
                <FiImage size={16} className="text-[#F25C05]" /> 
                {broadcastMediaUrl && broadcastMediaType === 'image' ? 'Image Attached ✓' : 'Attach Image'}
              </button>
              <button 
                type="button" 
                disabled={uploadingBroadcastMedia}
                onClick={() => broadcastVideoInputRef.current?.click()} 
                className={`flex-1 py-3 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-all border ${
                  broadcastMediaUrl && broadcastMediaType === 'video'
                    ? (isDark ? 'bg-amber-500/20 text-amber-300 border-amber-500/50' : 'bg-amber-50 text-amber-800 border-amber-300')
                    : (isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-100 border-slate-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-200')
                }`}
              >
                <FiVideo size={16} className="text-[#F25C05]" /> 
                {broadcastMediaUrl && broadcastMediaType === 'video' ? 'Video Attached ✓' : 'Attach Video'}
              </button>
              <button 
                type="button" 
                disabled={uploadingBroadcastMedia}
                onClick={() => broadcastAudioInputRef.current?.click()} 
                className={`flex-1 py-3 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-all border ${
                  broadcastMediaUrl && broadcastMediaType === 'audio'
                    ? (isDark ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50' : 'bg-emerald-50 text-emerald-800 border-emerald-300')
                    : (isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-100 border-slate-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-200')
                }`}
              >
                <FiRadio size={16} className="text-[#F25C05]" /> 
                {broadcastMediaUrl && broadcastMediaType === 'audio' ? 'Audio (~0.3MB) ✓' : 'Attach Audio'}
              </button>
            </div>

            {/* Clean High-Contrast Media Preview Card */}
            {broadcastMediaUrl && (
              <div className={`relative rounded-2xl overflow-hidden border p-3 space-y-2 ${isDark ? 'bg-slate-950 border-amber-400/40' : 'bg-slate-50 border-amber-300'}`}>
                <div className={`flex items-center justify-between pb-1 px-1 border-b ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
                  <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider flex items-center gap-1.5">
                    <FiLock size={12} className="text-emerald-600" /> Attached Vault Asset (~0.3MB Optimized)
                  </span>
                  <button 
                    type="button" 
                    onClick={() => { setBroadcastMediaUrl(''); setBroadcastMediaType(''); }}
                    className="text-[11px] font-semibold text-rose-500 hover:text-rose-600 underline cursor-pointer"
                  >
                    Remove
                  </button>
                </div>

                {broadcastMediaType === 'image' ? (
                  <div className={`relative rounded-xl overflow-hidden border ${isDark ? 'bg-black/60 border-slate-800' : 'bg-white border-slate-200'}`}>
                    <img src={broadcastMediaUrl} alt="Broadcast Attachment" className="max-h-56 w-full object-contain mx-auto" />
                  </div>
                ) : broadcastMediaType === 'video' ? (
                  <div className={`relative rounded-xl overflow-hidden border ${isDark ? 'bg-black border-slate-800' : 'bg-white border-slate-200'}`}>
                    <video controls src={broadcastMediaUrl} className="max-h-56 w-full object-contain mx-auto" />
                  </div>
                ) : (
                  <div className={`relative rounded-xl overflow-hidden border p-2 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
                    <audio controls preload="metadata" crossOrigin="anonymous" className="w-full">
                      <source src={broadcastMediaUrl} type="audio/mpeg" />
                      <source src={broadcastMediaUrl} type="audio/wav" />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                )}

                <div className={`py-2 px-3 rounded-xl flex items-center justify-between text-[10px] font-mono border ${isDark ? 'bg-slate-900 border-emerald-500/30 text-emerald-400' : 'bg-white border-emerald-200 text-emerald-700'}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="truncate max-w-[220px]">{broadcastMediaUrl}</span>
                  </div>
                  <span className={`uppercase font-sans font-bold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{broadcastMediaType}</span>
                </div>
              </div>
            )}

            {/* Modal Actions */}
            <div className={`flex items-center justify-end gap-2 pt-3 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
              <button
                type="button"
                onClick={onClose}
                className={`px-5 py-2.5 rounded-xl font-bold text-xs transition-colors cursor-pointer border ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300 border-slate-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'}`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploadingBroadcastMedia}
                className="px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider bg-[#F25C05] hover:bg-[#d45004] text-white shadow-lg shadow-[#F25C05]/20 cursor-pointer transition-all flex items-center gap-1.5"
              >
                <FiZap size={14} /> Broadcast & Index Now 🚀
              </button>
            </div>
          </form>
        )}

      </div>
    </div>
  );
}
